import { ChartCardTitle } from '@frontend/chart/components';
import { ChartInfoKind } from '@frontend/chart/types';
import { EntityType, calculateTimeDifferenceBetweenDates } from '@frontend/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { TransactionTimerName } from '@frontend/transaction/types';
import { ReactNode } from 'react';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../../redux/store';
import { useInsightDetailMode } from '../insight-detail-mode.context';
import { useInsightDetailFilters } from '../insight-detail/insight-detail-filters.context';
import { InsightOpenDetailButton } from '../insight-open-detail-button.component';
import { StalePudoTransaction, fetchStalePudoTransactions } from './fetch-stale-pudo-transactions';

export interface StalePudoTransactionsListProps {
    title: ReactNode;
    infoKind: ChartInfoKind;
    listId: string;
    timerName: TransactionTimerName;
    accountId: string;
    feature?: Feature;
    application?: Application;
    /** When set, shows a top-left open-detail button beside the list title. */
    insightId?: string;
}

export const StalePudoTransactionsList = (props: StalePudoTransactionsListProps) => {
    const { openPlatformDetail } = usePlatformModalHost();
    const dispatch = useAppDispatch();
    const isDetailMode = useInsightDetailMode();
    const filters = useInsightDetailFilters();
    const application = props.application ?? Application.PUDO;
    const showOpenButton = Boolean(props.insightId) && !isDetailMode;
    const spotId = filters.active ? filters.spotId : null;
    // Pick-ups care about who receives; drop-offs care about who sends.
    const showSenders = props.timerName !== TransactionTimerName.TRANSACTION_PICK_UP_PENDING;
    const showReceivers = props.timerName !== TransactionTimerName.TRANSACTION_DROP_OFF_PENDING;

    return (
        <ReactiveObjectList<StalePudoTransaction>
            key={spotId ?? 'all'}
            id={props.listId}
            useStaticListId
            showCount
            label={
                <div className='flex items-start gap-3'>
                    {showOpenButton && props.insightId ? <InsightOpenDetailButton insightId={props.insightId} /> : null}
                    <ChartCardTitle
                        title={props.title}
                        infoKind={props.infoKind}
                        titleClassName='text-foreground-default text-2xl font-semibold'
                    />
                </div>
            }
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            cardClassName='flex h-full min-w-0 w-full flex-col overflow-hidden'
            cardBodyClassName='flex min-h-0 min-w-0 flex-1 flex-col'
            tableProps={{
                defaultHeight: true,
                pagination: {
                    hidePageSize: true,
                    allowPageButtonNavigation: {
                        bottomProps: {
                            hideLabels: true,
                            paginationNumberButtonAmount: 5
                        }
                    }
                }
            }}
            defaultListValues={{
                pageSize: 10,
                sorting: [{ id: 'start_timestamp', desc: false }]
            }}
            sortingColumns={['start_timestamp']}
            fetch={(arg) =>
                fetchStalePudoTransactions(
                    props.timerName,
                    props.accountId,
                    {
                        ...arg,
                        ...(spotId ? { spot_id: spotId } : {})
                    },
                    application,
                    props.feature
                )
            }
            onSelectElement={(transaction) => openPlatformDetail({ entityType: EntityType.TRANSACTION, id: transaction.id })}
            columns={{
                columns: ['tracking_number', ...(showSenders ? ['senders'] : []), ...(showReceivers ? ['receivers'] : []), 'start_timestamp', 'spot'],
                headerOverwrite: new Map([
                    ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['start_timestamp', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR],
                    ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT]
                ]),
                customCells: new Map([
                    [
                        'senders',
                        (obj) => {
                            const senderIds = obj.row.original.sender_ids;
                            if (!senderIds || senderIds.length === 0) {
                                return (
                                    <td className={defaultCellClassName}>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td className={defaultCellClassName}>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={senderIds[0]}
                                            entityType={EntityType.USER}
                                        />
                                        {senderIds.length > 1 && (
                                            <MultiEntityButton
                                                entities={senderIds.slice(1).map((id) => ({ entity_id: id }))}
                                                render={(entity) => (
                                                    <PlatformObjectBadge
                                                        id={entity.entity_id}
                                                        entityType={EntityType.USER}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'receivers',
                        (obj) => {
                            const receiverIds = obj.row.original.receiver_ids;
                            if (!receiverIds || receiverIds.length === 0) {
                                return (
                                    <td className={defaultCellClassName}>
                                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                    </td>
                                );
                            }
                            return (
                                <td className={defaultCellClassName}>
                                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                                        <PlatformObjectBadge
                                            id={receiverIds[0]}
                                            entityType={EntityType.USER}
                                        />
                                        {receiverIds.length > 1 && (
                                            <MultiEntityButton
                                                entities={receiverIds.slice(1).map((id) => ({ entity_id: id }))}
                                                render={(entity) => (
                                                    <PlatformObjectBadge
                                                        id={entity.entity_id}
                                                        entityType={EntityType.USER}
                                                    />
                                                )}
                                            />
                                        )}
                                    </div>
                                </td>
                            );
                        }
                    ],
                    [
                        'start_timestamp',
                        (obj) => (
                            <td className='text-foreground-default truncate p-2 text-sm font-medium'>
                                {calculateTimeDifferenceBetweenDates(obj.row.original.timer_start_timestamp, new Date().toISOString())}
                            </td>
                        )
                    ],
                    [
                        'spot',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                {obj.row.original.spot_id ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.spot_id}
                                        entityType={EntityType.SPOT}
                                    />
                                ) : (
                                    <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                )}
                            </td>
                        )
                    ]
                ])
            }}
        />
    );
};
